<template>
  <UnsavedChangesModal ref="unsavedChangesModal" />

  <div class="container-fluid">
    <div class="d-flex align-items-center mt-2">
      <button class="btn btn-primary me-2" @click="save" :disabled="isSaving">
        <div v-if="isSaving" class="spinner-border spinner-border-sm"></div>
        <font-awesome-icon v-else :icon="['fas', 'save']" />
        {{ $t('action.save') }}
      </button>
      <div class="input-group" style="max-width: 300px;">
        <span class="input-group-text">
          <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
        </span>
        <input id="itemFilter" type="search" class="form-control" v-model="itemFilter" :placeholder="$t('action.search')"/>
        <button v-if="itemFilter" type="button" class="btn btn-outline-secondary" @click="clearFilter" :title="$t('action.clear')">
          <font-awesome-icon :icon="['fas', 'filter-slash']" />
        </button>
      </div>
    </div>
    <div id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
      <table class="table table-hover table-sm table-fit align-middle">
        <thead>
          <tr>
            <th class="mel-bar"></th>
            <th>
              {{ $t('field.generic.item') }}
              <font-awesome-icon :icon="['fas', 'sort-up']" />
            </th>
            <th>{{ $t('field.foreignItem.itemCode') }}</th>
            <th>{{ $t('field.masterdata.trackStock') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(entry, index) in filteredItemsList" :key="index" :class="{ 'is-filled': entry.IsSelected }">
            <td class="mel-bar" :class="{ 'mel-bar-primary': entry.IsSelected }"></td>
            <td><ItemField :item="entry.item" /></td>
            <td><input class="form-control" type="text" v-model="entry.code" maxlength="20" /></td>
            <td><input class="form-check-input" type="checkbox" v-model="entry.trackStock"></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { Id, toast, type ToastOptions } from 'vue3-toastify';
import { useSessionStore, useItemsStore, useReplacementItemsStore, useForeignItemsStore } from '@/stores';
import { ItemField } from '@/components/fields';
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue';
import { useUnsavedChangesGuard } from '@/services/unsavedChangesGuard';
import SyncForeignItemsRequest, { SyncForeignItemRequest } from '@/models/requests/SyncForeignItemsRequest';
import Item from '@/models/frontend/Item';

const i18n = useI18n();
const sessionStore = useSessionStore();
const itemsStore = useItemsStore();
const foreignItemsStore = useForeignItemsStore();
const replacementItemsStore = useReplacementItemsStore();

const { selectedSiteId } = storeToRefs(sessionStore);

const isSaving = ref(false);
const toastId = ref('' as Id);
const itemsList = ref([] as SelectableItem[]);
const offsetTop = ref(0);
const itemFilter = ref('');
const savedSnapshot = ref('');
const unsavedChangesModal = ref<InstanceType<typeof UnsavedChangesModal> | null>(null);

class SelectableItem {
  constructor(
    public item: Item,
    public code: string | undefined,
    public trackStock: boolean,
  ) { }

  get IsSelected(): boolean {
    return this.code !== undefined;
  }
}

const computeSnapshot = () => JSON.stringify(itemsList.value.map(e => ({
  id: e.item.id,
  code: e.code ?? '',
  trackStock: e.trackStock,
})));

const hasUnsavedChanges = computed(() => computeSnapshot() !== savedSnapshot.value);

useUnsavedChangesGuard(() => hasUnsavedChanges.value, unsavedChangesModal);

onMounted(async () => {
  for (const item of itemsStore.data) {
    // skip items that are replacements
    if (replacementItemsStore.getBaseItem(item.id) !== undefined) {
      continue;
    }

    const foreignItem = foreignItemsStore.getByItemId(item.id);
    itemsList.value.push(new SelectableItem(item, foreignItem?.code, foreignItem?.trackStock ?? true));
  }
  itemsList.value.sort((a, b) => a.item?.name.localeCompare(b.item?.name ?? '') ?? 0);
  savedSnapshot.value = computeSnapshot();

  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const save = async () => {
  isSaving.value = true;
  const requestItems = itemsList.value
    .filter(x => x.code !== undefined)
    .map((e) => new SyncForeignItemRequest(e.item.id, e.code!, e.trackStock));
  const requestData = new SyncForeignItemsRequest(selectedSiteId.value, requestItems);
  await foreignItemsStore.sync(requestData, onApiFulfilled, onApiRejected);
};

const onApiFulfilled = async () => {
  isSaving.value = false;
  savedSnapshot.value = computeSnapshot();
  toast.update(toastId.value, {
    render: i18n.t('statement.foreignItemSaved'),
    type: toast.TYPE.SUCCESS,
    isLoading: false,
    autoClose: 10000,
    closeOnClick: true
  } as ToastOptions);
};

const onApiRejected = (error: Error) => {
  isSaving.value = false;
  toast.update(toastId.value, {
    render: i18n.t('error.apiError', { error: error.message }),
    closeOnClick: true,
    type: toast.TYPE.ERROR,
    isLoading: false
  } as ToastOptions);
};

const filteredItemsList = computed(() => {
  if (!itemFilter.value.trim()) {
    return itemsList.value;
  }
  
  const filterText = itemFilter.value.toLowerCase().trim();
  return itemsList.value.filter(entry => {
    const item = entry.item;
    return (
      item.name.toLowerCase().includes(filterText) ||
      item.code.toLowerCase().includes(filterText) ||
      (item.description && item.description.toLowerCase().includes(filterText))
    );
  });
});

const clearFilter = () => {
  itemFilter.value = '';
};
</script>

<style scoped type="scss">
tr.is-filled>td {
  background-color: var(--mel-row-filled-bg);
}

table td.mel-bar,
table th.mel-bar
{
  padding-left: 0;
  padding-right: 0;
  width: 6px;
  min-width: 6px;
  max-width: 6px;
}

.table > :not(caption) > * > .mel-bar-none,
.mel-bar-none {
  background-color: transparent;
}
.table > :not(caption) > * > .mel-bar-secondary,
.mel-bar-secondary {
  background-color: var(--bs-body-bg);
}
.table > :not(caption) > * > .mel-bar-primary,
.mel-bar-primary {
  background-color: var(--bs-primary-bg-subtle);
}
.table > :not(caption) > * > .mel-bar-success,
.mel-bar-success {
  background-color: var(--bs-success-bg-subtle);
}
.table > :not(caption) > * > .mel-bar-warning,
.mel-bar-warning {
  background-color: var(--bs-warning-bg-subtle);
}
.table > :not(caption) > * > .mel-bar-danger,
.mel-bar-danger {
  background-color: var(--bs-danger-bg-subtle);
}
</style>
